import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { useDispatch } from 'react-redux';
import { useHistory, useParams } from 'react-router-dom';
import {
    Box,
    Chip,
    Divider,
    IconButton,
    InputAdornment,
    Table,
    TableBody,
    TableContainer,
    TableHead,
    TablePagination,
    TableRow,
    TextField,
    Tooltip,
    Typography,
    makeStyles,
} from '@material-ui/core';
import ArrowBackIcon from '@material-ui/icons/ArrowBack';
import ClearIcon from '@material-ui/icons/Clear';
import EditIcon from '@material-ui/icons/Edit';
import RefreshIcon from '@material-ui/icons/Refresh';
import SearchIcon from '@material-ui/icons/Search';
import { Spinner } from '../../components/UI';
import { GetBluedartStoreHubs } from '../../store/actions/settingsActionCreator';
import { getHubMasterPermission } from '../../utils/permissions';
import BluedartHubModal from '../../components/Modals/BluedartHubModal';
import {
    ActionIcon,
    HubTableCell,
    HubTableHeadCell,
} from '../hub-management/utility.component.hub';
import useHubManagementStyles from '../hub-management/useHubManagementStyles';

/**
 * Local to this page. The shared tableContainer is transparent, which works on OPS Management
 * because its table fills the viewport — here the listing is short and the rows end up floating
 * on the page background with nothing marking where it stops. Tinting the card and keeping the
 * rows white gives the same card-on-surface look the toolbar already has.
 */
const useStoreHubStyles = makeStyles({
    listingCard: {
        backgroundColor: '#fafbff',
        border: '1px solid #e6e8f2',
        borderRadius: 12,
        padding: '4px 12px 12px',
    },
    row: {
        backgroundColor: '#fff',
        transition: 'background-color 0.15s ease-in-out',
        '&:hover': {
            backgroundColor: '#f3f4fd',
        },
    },
});

const COLUMNS = [
    { key: 'name', label: 'Hub Name', minWidth: 220 },
    { key: 'vendorName', label: 'Vendor', minWidth: 180 },
    { key: 'cityName', label: 'City', minWidth: 120 },
    { key: 'address', label: 'Address', minWidth: 200 },
    { key: 'status', label: 'Status', minWidth: 100 },
];

/**
 * The hubs at one Blue Dart store.
 *
 * <p>OPS Management lists stores, not hubs — several hubs share a store id, one per vendor, and
 * the grid renders the oldest of them. Editing a hub needs the individual rows, which that
 * collapse hides, so this page is where they live.
 *
 * <p>Search filters the rows already in hand rather than re-querying, so it applies as you type
 * and there is no Search button: the endpoint returns a store's hubs in one call and a store
 * holds few of them. A button here would only add a click. If a store ever holds enough hubs to
 * need paging, the endpoint grows a search parameter and this becomes a submitted search.
 */
const StoreHubsPage: React.FC = () => {
    const { storeId } = useParams<{ storeId: string }>();
    const dispatch = useDispatch();
    const history = useHistory();
    const styles: any = useHubManagementStyles({ containerHeight: 0 });
    const local = useStoreHubStyles();
    const hubMasterPermissions = getHubMasterPermission();

    const [hubs, setHubs] = useState<any[]>([]);
    const [zone, setZone] = useState<string | null>(null);
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState<string | null>(null);
    const [searchText, setSearchText] = useState('');
    // 50 so nothing paginates at today's sizes — the busiest store holds 27 hubs. This is headroom
    // for a store that keeps gaining a hub per new vendor, not a change to how it reads now.
    const [page, setPage] = useState(0);
    const [rowsPerPage, setRowsPerPage] = useState(50);
    const [editHub, setEditHub] = useState<any>(null);

    const load = useCallback(() => {
        if (!storeId) {
            return;
        }
        setLoading(true);
        setError(null);
        dispatch(
            GetBluedartStoreHubs(
                storeId,
                (response: any) => {
                    setLoading(false);
                    setHubs(response?.response?.hubs ?? []);
                    setZone(response?.response?.zone ?? null);
                },
                // An out-of-scope store and an unknown id both come back as a message rather than
                // an empty list, so show it instead of a table that reads as "no hubs exist".
                (err: any) => {
                    setLoading(false);
                    setHubs([]);
                    setError(
                        err?.status?.message ??
                            'Could not load hubs for this store.',
                    );
                },
            ),
        );
    }, [dispatch, storeId]);

    useEffect(() => {
        load();
    }, [load]);

    const visibleHubs = useMemo(() => {
        const term = searchText.trim().toLowerCase();
        if (!term) {
            return hubs;
        }
        return hubs.filter((hub: any) =>
            [hub?.name, hub?.vendorName, hub?.cityName, hub?.address]
                .filter(Boolean)
                .some((value: string) =>
                    String(value).toLowerCase().includes(term),
                ),
        );
    }, [hubs, searchText]);

    // Paged from the FILTERED set, never the raw one — otherwise a search while on page 2 pages
    // through rows that no longer match.
    const pagedHubs = useMemo(
        () => visibleHubs.slice(page * rowsPerPage, (page + 1) * rowsPerPage),
        [visibleHubs, page, rowsPerPage],
    );

    // Any narrowing shortens the result set, so the page you were on may no longer exist.
    useEffect(() => {
        setPage(0);
    }, [searchText]);

    const columnCount = COLUMNS.length + (hubMasterPermissions.update ? 1 : 0);

    return (
        <Spinner loading={loading}>
            {/* styles.toolbar is already a flex row with its own gap — children sit in it
                directly rather than inside another flex box. */}
            <Box mb={1} className={styles.toolbar}>
                <Tooltip title="Back to OPS Management" arrow>
                    <IconButton
                        size="small"
                        onClick={() => history.push('/ops-management')}
                        style={{
                            border: '1px solid #ebedf8',
                            backgroundColor: '#fff',
                            marginRight: 4,
                        }}
                    >
                        <ArrowBackIcon fontSize="small" />
                    </IconButton>
                </Tooltip>
                <Box
                    display="flex"
                    alignItems="center"
                    style={{ gap: 10, minWidth: 0 }}
                >
                    <Typography
                        style={{
                            fontSize: 16,
                            fontWeight: 700,
                            color: '#14112d',
                            whiteSpace: 'nowrap',
                        }}
                    >
                        {storeId}
                    </Typography>
                    <Chip
                        size="small"
                        label={zone ?? 'Zone not set'}
                        style={{
                            height: 22,
                            fontSize: 12,
                            fontWeight: 700,
                            letterSpacing: '0.3px',
                            // Muted when unset: an unzoned store is invisible to every
                            // zone-scoped user, so it should not read as a normal value.
                            backgroundColor: zone ? '#f4ebfc' : '#f1f2f6',
                            color: zone ? '#43419e' : '#8a92a6',
                        }}
                    />
                </Box>

                <Box
                    ml="auto"
                    display="flex"
                    alignItems="center"
                    style={{ gap: 8 }}
                >
                    {!!hubs.length && (
                        <Typography className={styles.resultSummary}>
                            {searchText.trim()
                                ? `${visibleHubs.length} of ${hubs.length} hubs`
                                : `${hubs.length} hubs`}
                        </Typography>
                    )}
                    {/* Filters what is already loaded, so it applies as you type. */}
                    <TextField
                        size="small"
                        variant="outlined"
                        placeholder="Search hub, vendor, city or address"
                        value={searchText}
                        onChange={(event) => setSearchText(event.target.value)}
                        style={{ width: 320 }}
                        InputProps={{
                            startAdornment: (
                                <InputAdornment position="start">
                                    <SearchIcon fontSize="small" />
                                </InputAdornment>
                            ),
                            endAdornment: !!searchText && (
                                <InputAdornment position="end">
                                    <IconButton
                                        size="small"
                                        onClick={() => setSearchText('')}
                                    >
                                        <ClearIcon fontSize="small" />
                                    </IconButton>
                                </InputAdornment>
                            ),
                        }}
                    />
                    <Tooltip title="Refresh" arrow>
                        <IconButton size="small" onClick={load}>
                            <RefreshIcon />
                        </IconButton>
                    </Tooltip>
                </Box>
            </Box>

            {error ? (
                <Box className={styles.emptyState}>
                    <Typography className={styles.emptyTitle}>
                        Could not load hubs
                    </Typography>
                    <Typography className={styles.emptySubtitle}>
                        {error}
                    </Typography>
                </Box>
            ) : !hubs.length && !loading ? (
                <Box className={styles.emptyState}>
                    <Typography className={styles.emptyTitle}>
                        No hubs at this store
                    </Typography>
                    <Typography className={styles.emptySubtitle}>
                        {`Nothing is mapped to ${storeId} yet.`}
                    </Typography>
                </Box>
            ) : (
                <TableContainer
                    className={`${styles.tableContainer} ${local.listingCard}`}
                >
                    <Table stickyHeader aria-label="Store hubs table">
                        <TableHead>
                            <TableRow>
                                {COLUMNS.map((column) => (
                                    <HubTableHeadCell
                                        key={column.key}
                                        style={{ minWidth: column.minWidth }}
                                    >
                                        {column.label}
                                    </HubTableHeadCell>
                                ))}
                                {hubMasterPermissions.update && (
                                    <HubTableHeadCell align="right">
                                        Action
                                    </HubTableHeadCell>
                                )}
                            </TableRow>
                        </TableHead>
                        <TableBody>
                            {!visibleHubs.length && (
                                <TableRow>
                                    <HubTableCell colSpan={columnCount}>
                                        <Typography
                                            className={styles.emptySubtitle}
                                        >
                                            No hubs match this search.
                                        </Typography>
                                    </HubTableCell>
                                </TableRow>
                            )}
                            {pagedHubs.map((hub: any) => (
                                <React.Fragment key={hub?.id}>
                                    <TableRow className={local.row}>
                                        <HubTableCell>
                                            <Box
                                                display="flex"
                                                alignItems="center"
                                                style={{ gap: 8 }}
                                            >
                                                <Typography
                                                    className={
                                                        styles.tableValue
                                                    }
                                                >
                                                    {hub?.name ?? '-/-'}
                                                </Typography>
                                                {hub?.primary && (
                                                    <Chip
                                                        size="small"
                                                        label="Primary"
                                                        className={styles.chip}
                                                        style={{
                                                            backgroundColor:
                                                                '#F4EBFC',
                                                            color: '#43419E',
                                                        }}
                                                    />
                                                )}
                                            </Box>
                                        </HubTableCell>
                                        <HubTableCell>
                                            <Typography
                                                className={styles.tableValue}
                                            >
                                                {hub?.vendorName ?? '-/-'}
                                            </Typography>
                                        </HubTableCell>
                                        <HubTableCell>
                                            <Typography
                                                className={styles.tableValue}
                                            >
                                                {hub?.cityName ?? '-/-'}
                                            </Typography>
                                        </HubTableCell>
                                        <HubTableCell>
                                            <Typography
                                                className={styles.tableValue}
                                            >
                                                {hub?.address ?? '-/-'}
                                            </Typography>
                                        </HubTableCell>
                                        <HubTableCell>
                                            <Typography
                                                className={styles.tableValue}
                                                style={{
                                                    color: hub?.isActive
                                                        ? '#1F9254'
                                                        : '#A30D11',
                                                }}
                                            >
                                                {hub?.isActive
                                                    ? 'Active'
                                                    : 'Inactive'}
                                            </Typography>
                                        </HubTableCell>
                                        {/* Hidden rather than disabled: a disabled control still
                                            advertises a capability the backend will refuse. */}
                                        {hubMasterPermissions.update && (
                                            <HubTableCell align="right">
                                                <ActionIcon
                                                    style={{
                                                        borderRadius: 8,
                                                        padding: 6,
                                                    }}
                                                    color="primary"
                                                    onClick={() =>
                                                        setEditHub(hub)
                                                    }
                                                >
                                                    <EditIcon fontSize="small" />
                                                    <Typography
                                                        style={{
                                                            marginLeft: '8px',
                                                        }}
                                                    >
                                                        Edit
                                                    </Typography>
                                                </ActionIcon>
                                            </HubTableCell>
                                        )}
                                    </TableRow>
                                    <Divider
                                        style={{
                                            margin: '4px 0',
                                            backgroundColor: '#fff',
                                        }}
                                    />
                                </React.Fragment>
                            ))}
                        </TableBody>
                    </Table>
                </TableContainer>
            )}

            {/* Only once there is more than a page's worth. Today every store fits in one, so
                this stays out of the way until a store actually grows into it. */}
            {!error && visibleHubs.length > 25 && (
                <TablePagination
                    component="div"
                    count={visibleHubs.length}
                    page={page}
                    rowsPerPage={rowsPerPage}
                    rowsPerPageOptions={[25, 50, 100]}
                    onChangePage={(_e: any, next: number) => setPage(next)}
                    onChangeRowsPerPage={(event: any) => {
                        setRowsPerPage(parseInt(event.target.value, 10));
                        setPage(0);
                    }}
                />
            )}

            {!!editHub && (
                <BluedartHubModal
                    open
                    hub={editHub}
                    storeId={storeId}
                    zone={zone ?? undefined}
                    onClose={() => setEditHub(null)}
                    onSuccess={() => {
                        setEditHub(null);
                        load();
                    }}
                />
            )}
        </Spinner>
    );
};

export default StoreHubsPage;
